import { brandPalette } from "../../../constants/brandPalette";
import {
  Box,
  Button,
  Flex,
  Grid,
  IconButton,
  SimpleGrid,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import {
  RiCheckLine,
  RiDeleteBinLine,
  RiQrScan2Line,
} from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import {
  openStudentReportModel,
  openUserQRCodeModel,
} from "../../../modules/admin-modules/models/Actions";
import {
  acceptRequestWithoutVoucherRequest,
  deleteRequestRequest,
  editRequestRequest,
} from "../../../modules/admin-modules/video-requests/Actions";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
  canvas: brandPalette.warmBeige,
  muted: "rgba(69, 11, 17, 0.65)",
};

const PER_PAGE = 10;

const VideosRequestTable = ({ setSelectedData, searchTerm = "" }) => {
  const videos = useSelector((state) => state.videoRequests.requests);
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.videoRequests.isLoading);
  const [page, setPage] = useState(1);

  const filteredVideos = useMemo(() => {
    if (!videos) return [];
    if (!searchTerm?.trim()) return videos;
    const searchLower = searchTerm.toLowerCase();
    return videos.filter((data) => {
      const videoTitle = String(data?.video?.title || "").toLowerCase();
      const studentName = String(data?.student?.name || "").toLowerCase();
      return videoTitle.includes(searchLower) || studentName.includes(searchLower);
    });
  }, [videos, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredVideos.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const sliceStart = (safePage - 1) * PER_PAGE;
  const pageRows = filteredVideos.slice(sliceStart, sliceStart + PER_PAGE);

  if (isLoading) {
    return (
      <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height="280px" borderRadius="16px" startColor="gray.200" endColor="gray.300" />
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  if (!filteredVideos.length) {
    return (
      <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
        <Box
          border="1px dashed"
          borderColor={C.border}
          borderRadius="16px"
          bg="rgba(255,255,255,0.65)"
          py={16}
          textAlign="center"
        >
          <Text fontWeight="800" color={C.primary} fontSize="lg">
            {searchTerm ? "No requests match your search" : "No video view requests"}
          </Text>
          <Text mt={2} color={C.muted} fontSize="sm" maxW="md" mx="auto">
            Pending student requests for additional views will show up here.
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
        {pageRows.map((data) => (
          <VStack
            key={data.id}
            align="stretch"
            spacing={4}
            border="1px solid"
            borderColor={C.border}
            borderRadius="16px"
            p={5}
            bg="rgba(255,255,255,0.92)"
            boxShadow="0 4px 18px rgba(69, 11, 17, 0.06)"
            transition="box-shadow 0.2s, border-color 0.2s"
            _hover={{
              boxShadow: "0 10px 28px rgba(69, 11, 17, 0.1)",
              borderColor: "rgba(91, 13, 22, 0.35)",
            }}
          >
            <Box minW={0}>
              <Text fontSize="xs" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.08em">
                Video
              </Text>
              <Text fontSize="lg" fontWeight="800" color={C.secondary} mt={1} noOfLines={3}>
                {data?.video?.title || "—"}
              </Text>
            </Box>

            <Box pt={1} borderTop="1px solid" borderColor="rgba(216, 206, 195, 0.6)">
              <Text fontSize="xs" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.08em">
                Student
              </Text>
              <Text
                as="button"
                type="button"
                textAlign="left"
                fontSize="sm"
                fontWeight="700"
                color={C.primary}
                mt={1}
                noOfLines={2}
                textDecoration="underline"
                cursor="pointer"
                bg="none"
                border="none"
                p={0}
                onClick={() => {
                  if (data?.student) {
                    setSelectedData(data.student);
                    dispatch(openStudentReportModel());
                  }
                }}
              >
                {data?.student?.name || "—"}
              </Text>
            </Box>

            <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
              <Box>
                <Text fontSize="xs" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.08em">
                  Group
                </Text>
                <Text fontSize="sm" fontWeight="700" color={C.secondary} mt={1}>
                  {data?.student_group?.name || "—"}
                </Text>
              </Box>
              <Box textAlign="right">
                <Text fontSize="xs" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.08em">
                  Payment exception
                </Text>
                <Text fontSize="sm" fontWeight="700" color={C.secondary} mt={1} noOfLines={2}>
                  {data?.payment_exception
                    ? typeof data.payment_exception === "object"
                      ? data.payment_exception?.name ?? "—"
                      : String(data.payment_exception)
                    : "None"}
                </Text>
              </Box>
            </Flex>

            <Flex flexWrap="wrap" gap={2} pt={2} borderTop="1px solid" borderColor="rgba(216, 206, 195, 0.6)">
              <Button
                size="sm"
                leftIcon={<RiCheckLine />}
                borderRadius="full"
                bg={C.primary}
                color={brandPalette.cream}
                fontWeight="700"
                flex="1"
                minW="120px"
                _hover={{ bg: C.secondary }}
                onClick={async () => {
                  const formData = new FormData();
                  formData.append("_method", "PUT");
                  formData.append("video_id", data?.video?.id);
                  await dispatch(
                    editRequestRequest({
                      formData,
                      id: data?.id,
                    })
                  );
                }}
              >
                Approve
              </Button>
              <Button
                size="sm"
                leftIcon={<RiCheckLine />}
                borderRadius="full"
                variant="outline"
                borderColor={C.border}
                color={C.secondary}
                fontWeight="700"
                flex="1"
                minW="120px"
                bg={C.canvas}
                _hover={{ bg: C.canvas, borderColor: C.primary }}
                onClick={() => dispatch(acceptRequestWithoutVoucherRequest(data?.id))}
              >
                No voucher
              </Button>
              <IconButton
                size="sm"
                aria-label="Student QR code"
                icon={<RiQrScan2Line />}
                borderRadius="full"
                variant="outline"
                borderColor={C.border}
                color={C.primary}
                _hover={{ bg: "rgba(91, 13, 22, 0.08)" }}
                onClick={() => {
                  setSelectedData(data);
                  dispatch(openUserQRCodeModel());
                }}
              />
              <IconButton
                size="sm"
                aria-label="Delete request"
                icon={<RiDeleteBinLine />}
                borderRadius="full"
                variant="outline"
                borderColor={C.border}
                color="red.600"
                _hover={{ bg: "rgba(254, 178, 178, 0.25)" }}
                onClick={() => dispatch(deleteRequestRequest(data?.id))}
              />
            </Flex>
          </VStack>
        ))}
      </Grid>

      {totalPages > 1 ? (
        <Flex mt={8} align="center" justify="center" gap={4} flexWrap="wrap">
          <Button
            size="sm"
            borderRadius="full"
            variant="outline"
            borderColor={C.border}
            color={C.primary}
            isDisabled={safePage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <Text fontSize="sm" fontWeight="700" color={C.secondary}>
            Page {safePage} of {totalPages}
          </Text>
          <Button
            size="sm"
            borderRadius="full"
            variant="outline"
            borderColor={C.border}
            color={C.primary}
            isDisabled={safePage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </Flex>
      ) : null}
    </Box>
  );
};

export default VideosRequestTable;
